import { useCallback, useEffect, useMemo } from "react";
import tmi, {} from "tmi.js";
import { typeSafeObjectEntries } from "../utils/helpers";
import { useAmbassadors } from "./useAmbassadors";
import useChannel from "./useChannel";
const parseCsvEnv = (env) => { var _a; return (_a = env === null || env === void 0 ? void 0 : env.split(",").map((str) => str.toLowerCase())) !== null && _a !== void 0 ? _a : []; };
const testChannelNames = parseCsvEnv(process.env.REACT_APP_TEST_CHANNEL_NAMES);
const defaultChannelNames = parseCsvEnv(process.env.REACT_APP_DEFAULT_CHANNEL_NAMES);
const extraChannelNames = parseCsvEnv(process.env.REACT_APP_EXTRA_CHANNEL_NAMES);
const privilegedUsers = parseCsvEnv(process.env.REACT_APP_CHAT_COMMANDS_PRIVILEGED_USERS);
export default function useChatCommand(callback) {
    const channel = useChannel();
    const channelNames = useMemo(() => Array.from(new Set([
        // Always connect to any test channels, for development.
        ...testChannelNames,
        // If we know what channel we're in, connect to it.
        // Otherwise, connect to the default channels.
        ...(channel ? [channel] : defaultChannelNames),
        // If we're not in a default channel, connect to the extra channels.
        // Extra channels are used for mod control during collaborations,
        //  so we don't need to connect to them if we're in a default channel.
        ...(channel && !defaultChannelNames.includes(channel)
            ? extraChannelNames
            : []),
    ].map((name) => name.toLowerCase()))), [channel]);
    const ambassadors = useAmbassadors();
    const commandsMap = useMemo(() => {
        const commands = new Map();
        if (ambassadors) {
            typeSafeObjectEntries(ambassadors).forEach(([key, ambassador]) => {
                ambassador.commands.forEach((command) => {
                    commands.set(command.toLowerCase(), key);
                });
            });
        }
        commands.set("welcome", "welcome");
        commands.set("refresh", "refresh");
        return commands;
    }, [ambassadors]);
    const messageHandler = useCallback((id, channel, tags, msg, self) => {
        var _a, _b, _c, _d, _e;
        // Ignore if user is not a moderator or broadcaster or test user
        if (!tags.mod &&
            !((_a = tags.badges) === null || _a === void 0 ? void 0 : _a.broadcaster) &&
            !privilegedUsers.includes((_c = (_b = tags.username) === null || _b === void 0 ? void 0 : _b.toLowerCase()) !== null && _c !== void 0 ? _c : ""))
            return;
        // Ignore echoed messages (messages sent by the bot) and messages that don't start with '!'
        if (self || !msg.startsWith("!"))
            return;
        const [commandName, ...args] = msg
            .slice(1)
            .trim()
            .replace(/\s+/g, " ")
            .split(" ");
        const command = commandName && commandsMap.get(commandName.toLowerCase());
        if (!command)
            return;
        // Handle any special commands + privilege edge cases
        if (command === "refresh" &&
            !privilegedUsers.includes((_e = (_d = tags.username) === null || _d === void 0 ? void 0 : _d.toLowerCase()) !== null && _e !== void 0 ? _e : ""))
            return;
        console.log(`*Twitch extension received command: ${command} (${args})*`, id);
        callback(command, args);
    }, [commandsMap, callback]);
    useEffect(() => {
        const id = Date.now();
        console.log("*Twitch extension is connecting to chat*", id);
        // Create the client
        const client = new tmi.Client({
            connection: {
                secure: true,
                reconnect: true,
            },
            channels: channelNames.map((name) => `#${name}`),
        });
        // Handle incoming messages
        client.on("message", (...args) => messageHandler(id, ...args));
        // Handle race condition where we connect after being unmounted
        let closing = false;
        client.on("connected", () => {
            // If we connected after being unmounted, disconnect (again)
            if (closing) {
                client
                    .disconnect()
                    .then(() => console.log("*Twitch extension disconnected from chat (after connecting)*", id));
                return;
            }
            console.log(`*Twitch extension is connected to chat: ${channelNames.join(", ")}*`, id);
        });
        // Connect to chat
        client.connect();
        // Disconnect from chat when unmounting
        return () => {
            closing = true;
            client
                .disconnect()
                .then(() => console.log("*Twitch extension disconnected from chat*", id));
        };
    }, [channelNames, messageHandler]);
}
